// BubbleCanvas.tsx - Fixed version with proper bubble handling
import React, { useState, useRef } from 'react';
import { useTool, TOOL_MODES, BUBBLE_TYPES } from '../../context/ToolContext';
import { useProject } from '../../context/ProjectContext';

// Define TypeScript interfaces for local use
interface BubbleElement {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  text: string;
  type: string;
  fontSize?: number;
  fontFamily?: string;
}

// Define a proper type for ToolContext
interface ToolContextType {
  currentMode: string;
  bubbleType: string;
  fontSize: number;
  fontFamily: string;
  selectedElement: string | null;
  selectElement: (id: string, type: string) => void;
  clearSelection: () => void;
  addToUndoStack: (action: any) => void;
}

const BubbleCanvas: React.FC = () => {
  const { 
    currentMode, 
    bubbleType,
    fontSize,
    fontFamily,
    selectedElement,
    selectElement,
    clearSelection,
    addToUndoStack
  } = useTool() as ToolContextType;
  
  // Access project context
  const projectContext = useProject();
  const { currentPageIndex, updatePage } = projectContext;
  const currentPage = projectContext.pages[currentPageIndex];
  
  // Local state for dragging
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const canvasRef = useRef<HTMLDivElement>(null);
  
  // Get bubbles from current page
  const bubbles: BubbleElement[] = currentPage && currentPage.bubbles ? currentPage.bubbles as BubbleElement[] : [];
  
  console.log('🎨 BubbleCanvas rendering with', bubbles.length, 'bubbles, mode:', currentMode);
  console.log('📍 Pointer events:', currentMode === TOOL_MODES.BUBBLE ? 'auto' : 'none');
  
  // Add a new speech bubble
  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    console.log('🖱️ Canvas clicked in BubbleCanvas, mode:', currentMode);
    
    // Only add bubbles in BUBBLE mode
    if (currentMode === TOOL_MODES.BUBBLE) {
      // Make sure click was directly on the canvas, not a bubble
      if (e.target !== e.currentTarget) {
        console.log('❌ Click was on child element, not canvas');
        return;
      }
      
      console.log('✅ Creating new bubble...');
      
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      console.log('Click position:', { x, y });
      
      const newBubble: BubbleElement = {
        id: Date.now().toString(),
        x: x - 75, // Center the bubble on click
        y: y - 50,
        width: 150,
        height: 100,
        text: "Type here...",
        type: bubbleType,
        fontSize,
        fontFamily
      };
      
      console.log('New bubble created:', newBubble);
      
      const updatedBubbles: BubbleElement[] = [...bubbles, newBubble];
      
      // Update page
      updatePage(currentPageIndex, {
        bubbles: updatedBubbles
      });
      
      // Add to undo stack
      addToUndoStack({
        type: 'ADD_BUBBLE',
        pageIndex: currentPageIndex,
        bubble: newBubble
      });
      
      // Auto-select the new bubble
      selectElement(newBubble.id, 'bubble');
      
      console.log('✅ Bubble creation complete!');
    } else if (currentMode === TOOL_MODES.SELECT) {
      // In select mode, clear selection if clicking on empty space
      if (e.target === e.currentTarget) {
        console.log('🎯 SELECT mode: Clearing selection');
        clearSelection();
      }
    }
  };
  
  // Update bubble text
  const updateBubbleText = (id: string, text: string): void => {
    const updatedBubbles = bubbles.map(bubble => 
      bubble.id === id ? { ...bubble, text } : bubble
    );
    
    updatePage(currentPageIndex, {
      bubbles: updatedBubbles
    });
  };
  
  // Handle bubble mouse down for dragging
  const handleBubbleMouseDown = (e: React.MouseEvent, bubbleId: string): void => {
    e.stopPropagation();
    
    // Select the bubble
    selectElement(bubbleId, 'bubble');
    
    // Start dragging
    setIsDragging(true);
    setDragStart({
      x: e.clientX,
      y: e.clientY
    });
    
    console.log('🖱️ Started dragging bubble:', bubbleId);
  };
  
  // Handle mouse move for dragging
  const handleMouseMove = (e: React.MouseEvent): void => {
    if (!isDragging || !selectedElement) return;
    
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    
    const updatedBubbles = bubbles.map(bubble => {
      if (bubble.id === selectedElement) {
        return {
          ...bubble,
          x: Math.max(0, bubble.x + dx),
          y: Math.max(0, bubble.y + dy)
        };
      }
      return bubble;
    });
    
    updatePage(currentPageIndex, {
      bubbles: updatedBubbles
    });
    
    setDragStart({
      x: e.clientX,
      y: e.clientY
    });
  };
  
  // Handle mouse up
  const handleMouseUp = (): void => {
    if (isDragging) {
      setIsDragging(false);
      console.log('🖱️ Stopped dragging');
      
      // Add to undo stack
      addToUndoStack({
        type: 'UPDATE_BUBBLE',
        pageIndex: currentPageIndex,
        bubbleId: selectedElement,
        properties: bubbles.find(b => b.id === selectedElement)
      });
    }
    if (isResizing) {
      setIsResizing(false);
      console.log('🖱️ Stopped resizing');
    }
  };
  
  // Resize bubble
  const handleBubbleResize = (
    e: React.MouseEvent,
    bubbleId: string
  ): void => {
    e.stopPropagation();
    setIsResizing(true);
    
    const bubble = bubbles.find(b => b.id === bubbleId);
    if (!bubble) return;
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = bubble.width;
    const startHeight = bubble.height;
    
    const onMouseMove = (moveEvent: MouseEvent): void => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      
      const updatedBubbles = bubbles.map(b => {
        if (b.id === bubbleId) {
          return {
            ...b,
            width: Math.max(100, startWidth + dx),
            height: Math.max(50, startHeight + dy)
          };
        }
        return b;
      });
      
      updatePage(currentPageIndex, {
        bubbles: updatedBubbles
      });
    };
    
    const onMouseUp = (): void => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setIsResizing(false);
      
      // Add to undo stack
      addToUndoStack({
        type: 'UPDATE_BUBBLE',
        pageIndex: currentPageIndex,
        bubbleId: bubbleId,
        properties: bubbles.find(b => b.id === bubbleId)
      });
    };
    
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };
  
  // Change bubble type
  const changeBubbleType = (id: string): void => {
    const types = Object.values(BUBBLE_TYPES) as string[];
    const updatedBubbles = bubbles.map(bubble => {
      if (bubble.id === id) {
        const currentIndex = types.indexOf(bubble.type);
        const nextIndex = (currentIndex + 1) % types.length;
        return { ...bubble, type: types[nextIndex] };
      }
      return bubble;
    });
    
    updatePage(currentPageIndex, {
      bubbles: updatedBubbles
    });
    
    // Add to undo stack
    addToUndoStack({
      type: 'UPDATE_BUBBLE',
      pageIndex: currentPageIndex,
      bubbleId: id,
      properties: updatedBubbles.find(b => b.id === id)
    });
  };
  
  // Delete bubble
  const deleteBubble = (id: string): void => {
    const bubbleToDelete = bubbles.find(b => b.id === id);
    const updatedBubbles = bubbles.filter(bubble => bubble.id !== id);
    
    updatePage(currentPageIndex, {
      bubbles: updatedBubbles
    });
    
    // Add to undo stack
    addToUndoStack({
      type: 'DELETE_BUBBLE',
      pageIndex: currentPageIndex,
      bubble: bubbleToDelete
    });
    
    clearSelection();
  };
  
  // Get bubble style based on its type - IMPROVED for kids
  const getBubbleStyle = (bubble: BubbleElement): React.CSSProperties => {
    const isSelected = selectedElement === bubble.id;
    
    // Base style - kid-friendly with Comic Sans and bright colors
    let style: React.CSSProperties = {
      position: 'absolute',
      left: `${bubble.x}px`,
      top: `${bubble.y}px`,
      width: `${bubble.width}px`,
      minHeight: `${bubble.height}px`,
      padding: '12px',
      backgroundColor: 'white',
      cursor: isDragging ? 'grabbing' : 'grab',
      zIndex: isSelected ? 20 : 15,
      fontFamily: bubble.fontFamily || 'Comic Sans MS, cursive, sans-serif',
      fontSize: `${bubble.fontSize || fontSize}px`,
      border: isSelected ? '3px dashed #007bff' : '2px solid black',
      userSelect: 'none',
      boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      wordWrap: 'break-word',
      lineHeight: '1.3'
    };
    
    // Add specific styles based on bubble type
    switch (bubble.type) {
      case BUBBLE_TYPES.SPEECH:
        style = {
          ...style,
          borderRadius: '20px',
          backgroundColor: '#ffffff'
        };
        break;
        
      case BUBBLE_TYPES.THOUGHT:
        style = {
          ...style,
          borderRadius: '50%',
          border: isSelected ? '3px dashed #007bff' : '2px dotted black',
          backgroundColor: '#f0f8ff'
        };
        break;
        
      case BUBBLE_TYPES.NARRATION:
        style = {
          ...style,
          borderRadius: '8px',
          backgroundColor: '#f8f8f8',
          border: isSelected ? '3px dashed #007bff' : '2px solid #666',
          fontStyle: 'normal'
        };
        break;
        
      case BUBBLE_TYPES.SHOUT:
        style = {
          ...style,
          borderRadius: '8px',
          border: isSelected ? '3px dashed #007bff' : '3px solid #ff5722',
          backgroundColor: '#fff8e1',
          fontWeight: 'bold',
          transform: 'rotate(-2deg)',
          color: '#d84315'
        };
        break;
        
      case BUBBLE_TYPES.WHISPER:
        style = {
          ...style,
          borderRadius: '20px',
          border: isSelected ? '3px dashed #007bff' : '2px dashed #aaa',
          backgroundColor: '#f8f8f8',
          fontStyle: 'italic',
          fontSize: `${(bubble.fontSize || fontSize) * 0.9}px`,
          color: '#666'
        };
        break;
    }
    
    return style;
  };
  
  // Get bubble type display name
  const getBubbleTypeName = (type: string): string => {
    const names: Record<string, string> = {
      [BUBBLE_TYPES.SPEECH]: '💬 Speech',
      [BUBBLE_TYPES.THOUGHT]: '💭 Thought',
      [BUBBLE_TYPES.NARRATION]: '📖 Story',
      [BUBBLE_TYPES.SHOUT]: '💥 Shout',
      [BUBBLE_TYPES.WHISPER]: '🤫 Whisper'
    };
    return names[type] || type;
  };
  
  // Render speech bubble tail
  const renderSpeechTail = (bubble: BubbleElement, isSelected: boolean) => {
    if (bubble.type !== BUBBLE_TYPES.SPEECH) return null;
    
    return (
      <div 
        className="absolute"
        style={{
          bottom: '-12px',
          left: '30px',
          width: '0',
          height: '0',
          borderLeft: '12px solid transparent',
          borderRight: '12px solid transparent',
          borderTop: '12px solid white',
          zIndex: 10
        }}
      />
    );
  };
  
  // Render thought bubble dots
  const renderThoughtDots = (bubble: BubbleElement) => {
    if (bubble.type !== BUBBLE_TYPES.THOUGHT) return null;
    
    return (
      <>
        <div 
          className="absolute bg-white border-2 border-black rounded-full"
          style={{
            bottom: '-18px',
            left: '40px',
            width: '10px',
            height: '10px'
          }}
        />
        <div 
          className="absolute bg-white border-2 border-black rounded-full"
          style={{
            bottom: '-28px',
            left: '35px',
            width: '7px',
            height: '7px'
          }}
        />
        <div 
          className="absolute bg-white border-2 border-black rounded-full"
          style={{
            bottom: '-35px',
            left: '32px',
            width: '5px',
            height: '5px'
          }}
        />
      </>
    );
  };
  
  return (
    <div 
      ref={canvasRef}
      className="absolute inset-0 bubble-canvas"
      onClick={handleCanvasClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{ 
        // Only capture events for BUBBLE mode, let SELECT and drawing modes pass through to DrawingCanvas
        pointerEvents: currentMode === TOOL_MODES.BUBBLE ? 'auto' : 'none',
        cursor: currentMode === TOOL_MODES.BUBBLE ? 'crosshair' : 'default'
      }}
    >
      {/* Debug indicator when in bubble mode */}
      {currentMode === TOOL_MODES.BUBBLE && (
        <div 
          className="absolute top-2 left-2 z-50 bg-cyan-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-lg"
          style={{ pointerEvents: 'none' }}
        >
          💬 Click to add bubble! ({bubbles.length} bubbles)
        </div>
      )}
      
      {bubbles.map(bubble => {
        const isSelected = selectedElement === bubble.id;
        const bubbleStyle = getBubbleStyle(bubble);
        
        return (
          <div 
            key={bubble.id}
            className="bubble"
            style={{
              ...bubbleStyle,
              // Always enable pointer events for individual bubbles so they can be selected
              pointerEvents: 'auto'
            }}
            onMouseDown={(e) => handleBubbleMouseDown(e, bubble.id)}
            onClick={(e) => {
              e.stopPropagation();
              // Always allow bubble selection regardless of current mode
              selectElement(bubble.id, 'bubble');
            }}
          >
            {/* Speech bubble tail */}
            {renderSpeechTail(bubble, isSelected)}
            
            {/* Thought bubble dots */}
            {renderThoughtDots(bubble)}
            
            {/* Bubble content */}
            {isSelected ? (
              <div className="w-full h-full flex flex-col">
                <textarea
                  className="flex-1 w-full bg-transparent border-none outline-none resize-none text-center"
                  value={bubble.text}
                  onChange={(e) => updateBubbleText(bubble.id, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{
                    fontSize: `${bubble.fontSize || fontSize}px`,
                    fontFamily: bubble.fontFamily || 'Comic Sans MS, cursive, sans-serif',
                    fontWeight: bubble.type === BUBBLE_TYPES.SHOUT ? 'bold' : 'normal',
                    fontStyle: bubble.type === BUBBLE_TYPES.WHISPER ? 'italic' : 'normal',
                    color: bubble.type === BUBBLE_TYPES.SHOUT ? '#d84315' : 
                           bubble.type === BUBBLE_TYPES.WHISPER ? '#666' : 'black'
                  }}
                  placeholder="Type your text here..."
                  autoFocus
                />
                
                {/* Control buttons - made more kid-friendly */}
                <div className="flex justify-between mt-2 gap-1">
                  <button 
                    className="bg-blue-500 text-white px-3 py-1 text-xs rounded-full hover:bg-blue-600 transition-colors font-bold"
                    onClick={(e) => {
                      e.stopPropagation();
                      changeBubbleType(bubble.id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {getBubbleTypeName(bubble.type)}
                  </button>
                  <button 
                    className="bg-red-500 text-white px-3 py-1 text-xs rounded-full hover:bg-red-600 transition-colors font-bold"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteBubble(bubble.id);
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ) : (
              <div 
                className="break-words w-full h-full flex items-center justify-center"
                style={{
                  fontWeight: bubble.type === BUBBLE_TYPES.SHOUT ? 'bold' : 'normal',
                  fontStyle: bubble.type === BUBBLE_TYPES.WHISPER ? 'italic' : 'normal',
                  color: bubble.type === BUBBLE_TYPES.SHOUT ? '#d84315' : 
                         bubble.type === BUBBLE_TYPES.WHISPER ? '#666' : 'black'
                }}
              >
                {bubble.text}
              </div>
            )}
            
            {/* Resize handle - only show when selected */}
            {isSelected && (
              <div
                className="absolute bottom-0 right-0 w-4 h-4 bg-blue-500 cursor-se-resize rounded-tl-lg"
                onMouseDown={(e) => handleBubbleResize(e, bubble.id)}
                style={{
                  borderTopLeftRadius: '4px'
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default BubbleCanvas;